import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Crown, Award, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyCertificatePage = () => {
  const { certificateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        const response = await axios.get(`${API}/certificates/verify/${certificateId}`);
        setCertificate(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Certificate not found' : 'Failed to verify certificate');
      } finally {
        setLoading(false);
      }
    };

    if (certificateId) {
      verifyCertificate();
    }
  }, [certificateId]);

  const formattedDate = certificate?.completion_date 
    ? new Date(certificate.completion_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '';

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <SEO 
        title="Verify Certificate | Queen B Academy"
        description="Verify the authenticity of a Queen B Academy certificate"
      />
      
      <div className="max-w-2xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className="w-8 h-8 text-[#D4AF37]" />
            <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#D4AF37] uppercase tracking-wider">
              Queen B Academy
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Certificate Verification</h1>
          <p className="text-white/50">Verify the authenticity of a Queen B Academy certificate</p>
        </div>

        {/* Verification Result */}
        <div className="bg-[#121212] border border-white/10 p-8">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto animate-spin mb-4" />
              <p className="text-white/50">Verifying certificate...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-red-400 mb-2">Invalid Certificate</h2>
              <p className="text-white/50 mb-6">{error}</p>
              <p className="text-white/40 text-sm">
                Certificate ID: <span className="font-mono text-white/60">{certificateId}</span>
              </p>
            </div>
          ) : certificate?.valid ? (
            <div className="text-center">
              {/* Valid Badge */}
              <div className="w-20 h-20 rounded-full bg-green-500/10 border-2 border-green-500 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              
              <h2 className="text-xl font-bold text-green-400 mb-6">Certificate Verified</h2>
              
              {/* Certificate Details */}
              <div className="space-y-4 text-left max-w-md mx-auto">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-white/50">Certificate ID</span>
                  <span className="text-white font-mono text-sm">{certificate.certificate_id}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-white/50">Student Name</span>
                  <span className="text-white font-medium">{certificate.student_name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-white/50">Course</span>
                  <span className="text-[#D4AF37] font-medium">{certificate.course_name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-white/50">Completion Date</span>
                  <span className="text-white">{formattedDate}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-white/50">Issued By</span>
                  <span className="text-white">{certificate.issued_by}</span>
                </div>
              </div>

              {/* Seal */}
              <div className="mt-8 flex items-center justify-center gap-3">
                <Award className="w-6 h-6 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-medium uppercase tracking-wider text-sm">
                  Authentic Certificate
                </span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Info */}
        <div className="mt-8 text-center text-white/40 text-sm">
          <p>This verification confirms that the certificate was issued by Queen B Logistics Academy.</p>
          <p className="mt-2">
            Questions? Contact us at{' '}
            <a href="mailto:academy@queenblogistics.com" className="text-[#D4AF37] hover:underline">
              academy@queenblogistics.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificatePage;
